import { useState, type FormEvent } from "react";
import { inquiryCategories } from "@/mocks/brands";
import usePageSEO from "@/hooks/usePageSEO";
import JsonLd from "@/components/feature/JsonLd";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://ghbeauty.kr";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": siteUrl + "/" },
    { "@type": "ListItem", "position": 2, "name": "Contact", "item": siteUrl + "/contact" }
  ]
};

// Replace YOUR_FILE_ID below with your Google Drive PDF file ID
const CATALOG_URL = "https://drive.google.com/uc?export=download&id=1AlhTbYS-9oQzkHhPnCWMXii0c30WiWu6";

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactPage() {
  usePageSEO({
    title: "Request a K-Beauty Wholesale Quote | GH Corporation",
  });

  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");
  const [textareaLen, setTextareaLen] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === "submitting") return;

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honey = (formData.get("website_alt") as string | null)?.trim();
    if (honey) {
      setStatus("success");
      setMessage(
        "Thank you. Your inquiry has been received — we normally respond within one business day."
      );
      form.reset();
      setTextareaLen(0);
      return;
    }
    formData.delete("website_alt");

    const details = (formData.get("project_details") as string) ?? "";
    if (details.length > 500) {
      setStatus("error");
      setMessage("Please keep project details within 500 characters.");
      return;
    }

    const required = ["full_name", "email", "company", "destination_country", "product_interest"];
    for (const r of required) {
      const v = formData.get(r);
      if (!v || (typeof v === "string" && !v.trim())) {
        setStatus("error");
        setMessage("Please complete all required fields marked with *");
        return;
      }
    }

    try {
      setStatus("submitting");
      setMessage("");
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (typeof value === "string") body.append(key, value);
      });

      const res = await fetch("/api/inquiry", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (!res.ok) throw new Error("Submission failed");

      setStatus("success");
      setMessage(
        "Thank you. Your inquiry has been received — we normally respond within one business day."
      );
      form.reset();
      setTextareaLen(0);
    } catch {
      setStatus("error");
      setMessage(
        "Sorry, we couldn't send your inquiry just now. Please try again, or email us at kwonkhkorea@gmail.com."
      );
    }
  };

  return (
    <section className="w-full px-6 md:px-12 lg:px-20 pt-28 pb-24 md:pb-32 bg-background-50">
      <JsonLd data={breadcrumbSchema} />
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-accent-500" />
              <span className="font-label text-[11px] tracking-[0.22em] uppercase text-accent-700">
                Wholesale Inquiry
              </span>
            </div>
            <h1 className="font-heading text-foreground-950 text-4xl md:text-5xl lg:text-[56px] leading-[1.05] font-light">
              Tell Us What You Need.
              <br />
              <span className="italic">We&apos;ll Prepare the Right Offer.</span>
            </h1>
            <p className="mt-8 text-foreground-700 text-[15px] md:text-base leading-relaxed font-light">
              Share your target brands, products, quantities and destination. Our export team will review your request and respond with availability, wholesale pricing, MOQ guidance and a recommended shipping option.
            </p>

            <ul className="mt-10 space-y-5">
              {[
                {
                  icon: "ri-user-line",
                  title: "PIC",
                  value: "Kihwan Kwon",
                },
                {
                  icon: "ri-mail-line",
                  title: "Email",
                  value: "kwonkhkorea@gmail.com",
                },
                {
                  icon: "ri-whatsapp-line",
                  title: "Mobile",
                  value: "+82-10-4270-0614",
                },
                {
                  icon: "ri-smartphone-line",
                  title: "Messaging",
                  value: "KakaoTalk · WhatsApp · Viber · Telegram available",
                },
                {
                  icon: "ri-map-pin-line",
                  title: "Office",
                  value: "114 Yeomjeon-ro, Michuhol-gu, Incheon, Republic of Korea",
                },
              ].map((c) => (
                <li key={c.title} className="flex items-start gap-4">
                  <span className="w-11 h-11 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-800 shrink-0">
                    <i className={`${c.icon} text-lg`} />
                  </span>
                  <div>
                    <div className="font-label text-[11px] tracking-[0.2em] uppercase text-foreground-500">
                      {c.title}
                    </div>
                    <div className="mt-1 text-foreground-950 text-base">{c.value}</div>
                  </div>
                </li>
              ))}
            </ul>

            <a
              href={CATALOG_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-10 block rounded-2xl bg-accent-100/60 border border-accent-200/60 p-6 cursor-pointer group hover:bg-accent-200/60 transition-colors"
            >
              <div className="flex items-start gap-4">
                <span className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-500 text-background-50 shrink-0 group-hover:scale-110 transition-transform">
                  <i className="ri-file-pdf-line text-xl" />
                </span>
                <div>
                  <div className="font-label text-[11px] tracking-[0.2em] uppercase text-accent-700">
                    Product Catalog
                  </div>
                  <div className="mt-1 text-foreground-950 text-base font-medium">
                    Download Our Brand Catalog
                  </div>
                  <p className="mt-2 text-foreground-600 text-[14px] leading-relaxed">
                    Full brand lineup with product categories, MOQ guidance, and shipping terms — ready for your review.
                  </p>
                  <span className="mt-4 inline-flex items-center gap-2 font-label text-[12px] tracking-[0.14em] uppercase text-accent-700">
                    <i className="ri-download-line" />
                    Download PDF
                  </span>
                </div>
              </div>
            </a>
          </div>

          <div className="lg:col-span-7">
            <p className="mb-6 p-5 rounded-2xl bg-background-100 border border-background-200/70 text-foreground-700 text-[14px] leading-relaxed font-light">
              Not sure about quantities, shipping terms or order size? Complete only the information you know, and our team will guide you.
            </p>

            <form
              id="buyer-inquiry-form"
              onSubmit={handleSubmit}
              className="rounded-3xl bg-background-100 border border-background-200/70 p-6 md:p-10"
            >
              <div className="gh-extra-field" aria-hidden="true">
                <label>
                  Website
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                  />
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Full Name *">
                  <input
                    type="text"
                    name="full_name"
                    required
                    placeholder="Jane Doe"
                    className={inputCls}
                  />
                </Field>
                <Field label="Business Email *">
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="jane@yourcompany.com"
                    className={inputCls}
                  />
                </Field>
                <Field label="Company *">
                  <input
                    type="text"
                    name="company"
                    required
                    placeholder="Company name"
                    className={inputCls}
                  />
                </Field>
                <Field label="Destination Country *">
                  <input
                    type="text"
                    name="destination_country"
                    required
                    placeholder="e.g. Mexico, UAE, USA"
                    className={inputCls}
                  />
                </Field>
                <Field label="Phone / WhatsApp">
                  <input
                    type="tel"
                    name="phone"
                    placeholder="+1 555 123 4567"
                    className={inputCls}
                  />
                </Field>
                <Field label="Business Type">
                  <select name="business_type" className={inputCls} defaultValue="">
                    <option value="" disabled>
                      Select…
                    </option>
                    <option>Distributor / Wholesaler</option>
                    <option>Retailer / Chain</option>
                    <option>E-commerce / Online Seller</option>
                    <option>Beauty Salon / Spa</option>
                    <option>Other</option>
                  </select>
                </Field>

                <Field label="Target Brand or Product">
                  <input
                    type="text"
                    name="target_brand"
                    placeholder="e.g. Anua, Torriden, Round Lab"
                    className={inputCls}
                  />
                </Field>
                <Field label="Estimated Quantity">
                  <input
                    type="text"
                    name="estimated_quantity"
                    placeholder="e.g. 500 units per SKU"
                    className={inputCls}
                  />
                </Field>

                <Field label="Product Interest *" full>
                  <select
                    name="product_interest"
                    required
                    className={inputCls}
                    defaultValue=""
                  >
                    <option value="" disabled>
                      Select a product category…
                    </option>
                    {inquiryCategories.map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </Field>

                <Field label="Preferred Shipping Mode">
                  <select name="preferred_shipping" className={inputCls} defaultValue="">
                    <option value="" disabled>
                      Select…
                    </option>
                    <option>FCL (Full Container Load)</option>
                    <option>LCL (Less than Container Load)</option>
                    <option>AIR Freight</option>
                    <option>Need Consultation</option>
                  </select>
                </Field>
                <Field label="Incoterms (if known)">
                  <select name="incoterms" className={inputCls} defaultValue="">
                    <option value="" disabled>
                      Select…
                    </option>
                    <option>FOB</option>
                    <option>CIF</option>
                    <option>EXW</option>
                    <option>DAP</option>
                    <option>Not Sure / Need Guidance</option>
                  </select>
                </Field>

                <Field label="Estimated Order Volume">
                  <select name="order_volume" className={inputCls} defaultValue="">
                    <option value="" disabled>
                      Select range…
                    </option>
                    <option>Trial Order</option>
                    <option>Under USD 5,000</option>
                    <option>USD 5,000–20,000</option>
                    <option>USD 20,000–50,000</option>
                    <option>USD 50,000–100,000</option>
                    <option>Over USD 100,000</option>
                  </select>
                </Field>
                <Field label="Target Timeline">
                  <input type="date" name="launch_date" className={inputCls} />
                </Field>

                <Field label="Project Details" full>
                  <textarea
                    name="project_details"
                    rows={5}
                    maxLength={500}
                    onChange={(e) => setTextareaLen(e.target.value.length)}
                    placeholder="Tell us about the brands you're interested in, target market, packaging requirements, certifications needed, etc."
                    className={`${inputCls} resize-none`}
                  />
                  <div className="mt-1.5 text-right font-label text-[11px] tracking-wider text-foreground-500">
                    {textareaLen} / 500
                  </div>
                </Field>
              </div>

              <div className="mt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
                <p className="text-foreground-500 text-[12px] leading-relaxed max-w-md">
                  By submitting this form, you agree that GH Corporation may contact you about your
                  inquiry. We never share your information with third parties.
                </p>
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors font-label text-[13px] tracking-[0.2em] uppercase disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      Request Wholesale Quotation
                      <i className="ri-arrow-right-up-line" />
                    </>
                  )}
                </button>
              </div>

              {message && (
                <div
                  className={`mt-6 p-4 rounded-xl text-sm ${
                    status === "success"
                      ? "bg-accent-100 text-accent-900 border border-accent-200"
                      : "bg-secondary-100 text-secondary-900 border border-secondary-200"
                  }`}
                >
                  {message}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

const inputCls =
  "w-full px-4 py-3 rounded-lg bg-background-50 border border-background-200/80 text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-200 transition-colors";

function Field({
  label,
  children,
  full,
}: {
  label: string;
  children: React.ReactNode;
  full?: boolean;
}) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="font-label text-[11px] tracking-[0.18em] uppercase text-foreground-600">
        {label}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}